import type { FetchResult, MutationUpdaterFn, PureQueryOptions, RefetchQueriesFunction } from "@apollo/client";
import { Mutation } from "@apollo/client/react/components";
import transformOnAssetDiscussionCommentWithRepliesCountFields from "components/discussions/apollo/transformOnAssetDiscussionCommentWithRepliesCountFields";
import { useAmplitude } from "../../../lib/amplitude/Amplitude";
import PostCommentOnAsset from "../../../lib/graphql/mutations/discussions/PostCommentOnAsset";
import type {
  IPostCommentOnAssetMutation,
  IPostCommentOnAssetMutationVariables,
} from "../../../lib/graphql/mutations/discussions/__generated__/PostCommentOnAsset.generated";
import type { IAssetType } from "../../../types/graphqlTypes";
import transformOnDiscussionCommentsRootQuery from "../apollo/transformOnDiscussionCommentsRootQuery";

interface IPostCommentOnAssetMutationArgs {
  assetType: IAssetType;
  assetId: string;
  bodyAsPlainText: string;
}

interface IPostCommentOnAssetMutationProps {
  children: (
    mutate: (args: IPostCommentOnAssetMutationArgs) => Promise<FetchResult<IPostCommentOnAssetMutation> | void>,
  ) => any;
  refetchQueries?: Array<string | PureQueryOptions> | RefetchQueriesFunction;
  update?: MutationUpdaterFn<IPostCommentOnAssetMutation>;
}

export default function PostCommentOnAssetMutation({
  children,
  refetchQueries,
  update,
}: IPostCommentOnAssetMutationProps) {
  const amplitude = useAmplitude();

  return (
    <Mutation<IPostCommentOnAssetMutation, IPostCommentOnAssetMutationVariables>
      mutation={PostCommentOnAsset}
      refetchQueries={refetchQueries}
    >
      {(postCommentOnAsset) => {
        return children(({ assetType, assetId, bodyAsPlainText }) => {
          amplitude.logEvent("Wrote a comment", {
            assetType,
            assetId,
            contentCharacterCount: bodyAsPlainText.length,
          });

          return postCommentOnAsset({
            variables: {
              assetType,
              assetId,
              bodyAsPlainText,
            },
            update: (dataProxy, queryResult) => {
              if (!queryResult.data || !queryResult.data.postCommentOnAsset) {
                return;
              }

              transformOnAssetDiscussionCommentWithRepliesCountFields(
                dataProxy,
                queryResult.data.postCommentOnAsset.asset._id,
                (fragmentData) => ({
                  ...fragmentData,
                  replies: {
                    ...fragmentData.replies,
                    totalCount: fragmentData.replies.totalCount + 1,
                  },
                }),
              );

              transformOnDiscussionCommentsRootQuery(
                dataProxy,
                queryResult.data.postCommentOnAsset.asset._id,
                (rootQueryData) => {
                  if (!queryResult || !queryResult.data || !queryResult.data.postCommentOnAsset) {
                    return rootQueryData;
                  }

                  const pinnedComments = rootQueryData.nodes.filter((node) => node.isPinned);
                  const otherComments = rootQueryData.nodes.filter((node) => !node.isPinned);
                  const nodesAfter = [...pinnedComments, queryResult.data.postCommentOnAsset, ...otherComments];

                  return {
                    ...rootQueryData,
                    totalCount:
                      nodesAfter.length > rootQueryData.nodes.length
                        ? Math.max(rootQueryData.totalCount + 1, 0)
                        : rootQueryData.totalCount,
                    nodes: nodesAfter,
                  };
                },
              );

              if (update) {
                update(dataProxy, queryResult);
              }
            },
          });
        });
      }}
    </Mutation>
  );
}
